import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {AddCompany} from '@components/add-company/add-company'
import {
  keyboardHandler,
  noPropagate,
  useClickAway,
  useSyncEvent
} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {Sync} from '../../../client/sync'
import {pendingCompanyUserStore} from '../../logged-in/pending-company-user-store'
import {
  CompaniesContext,
  CompaniesStore,
  useCompaniesStore
} from './companies-store'
import AddCompanyIcon from './icon-add-company.svg'
import CompanyIcon from './icon-company.svg'
import ConfigIcon from './icon-config.svg'
import DeleteIcon from './icon-delete.svg'
import EditIcon from './icon-edit.svg'

const TwIcon = tw.div`
  p-4
  w-18
  h-32
  box-content
  flex
  items-center
`

const TwActionIcon = tw.div<{$delete?: boolean}>`
  ${p =>
    p.$delete
      ? 'active:text-error-old hover:text-accent-old'
      : 'active:text-action-pressed hover:text-action-hover'}
  relative
  top-8
  inline-block
  w-32
  h-18
  bg-center
  bg-no-repeat
  cursor-pointer
  text-obscure-constant-old
`

const TwInput = tw.input`
  shadow-none
  focus:shadow-none
  hover:shadow-none
  all-unset
  overflow-hidden
  cursor-text
  bg-transparent
  w-full
  box-border
  py-6
  focus:border-0
  focus:bg-transparent
  hover:bg-transparent
  flex
`

const TbItemTw = tw.div<{$hoverTextMain?: boolean}>`
  ${p =>
    p.$hoverTextMain
      ? 'hover:text-main-old'
      : 'hover:text-action-hover active:text-action-pressed'}
  relative
  h-32
  flex
  items-center
  w-424
  leading-main
  text-obscure-constant-old
  text-14
  hover:bg-main-even-old
`

const TbItemLabelTw = tw.div`
  flex-1
  max-w-304
  self-center
  truncate
  ml-4
  cursor-default
`

const TwConfigure = tw.div`
  bg-center
  bg-no-repeat
  cursor-pointer
  pl-8
  mt-16
  hover:text-action-hover
  active:text-action-pressed
  flex
`

export const Companies = () => {
  const [store] = useState(() => new CompaniesStore())

  return (
    <CompaniesContext.Provider value={store}>
      <CompaniesBase />
    </CompaniesContext.Provider>
  )
}

// eslint-disable-next-line max-lines-per-function
const CompaniesBase = observer(() => {
  const ref = useRef<HTMLInputElement | null>(null)
  const [state, store] = useCompaniesStore()

  useEffect(() => {
    pendingCompanyUserStore.state.modified
  }, [pendingCompanyUserStore.state.modified])

  useSyncEvent(
    'profile',
    {
      onlyRemote: true,
      debounceTime: 50,
      filter: globals.currentUserId
    },
    () => {
      store.recalculateList()
    }
  )

  const onClickOutside = () => {
    if (state.companyName.length > 0) {
      store.editSubmit()
    }
    store.editCancel()
  }

  useClickAway(ref, onClickOutside)

  return (
    <>
      {state.all.map(
        // eslint-disable-next-line max-lines-per-function
        company => {
          return (
            <TbItemTw
              draggable
              style={
                company.id === state.draggedCompany
                  ? {
                      transition: '0.01s',
                      transform: 'translateX(-9999px)'
                    }
                  : {}
              }
              key={company.id}
              className={cn(
                company.id === globals.getCompanyId() && 'font-semibold'
              )}
              onDragStart={event => store.dragStart(company.id, event)}
              onDragEnter={() => store.dragEnter(company.id)}
              onDragEnd={() => store.dragEnd()}
              onDragOver={noPropagate}
              $hoverTextMain={state.mouseEnter}
            >
              <TwIcon
                className={cn(
                  company.id === globals.getCompanyId()
                    ? 'text-secondary-constant-old'
                    : 'hover:text-action-hover'
                )}
                onMouseEnter={() => {
                  store.setMouseEnter(true)
                }}
                onMouseLeave={() => {
                  store.setMouseEnter(false)
                }}
              >
                <CompanyIcon />
              </TwIcon>
              <div className="flex w-full h-32 hover:text-main-old text-main-old">
                {state.edited !== company.id && (
                  <TbItemLabelTw onClick={() => store.setCompany(company.id)}>
                    {company.name}
                  </TbItemLabelTw>
                )}

                {state.edited === company.id && (
                  <TbItemLabelTw>
                    <TwInput
                      ref={ref}
                      autoFocus
                      onFocus={e => {
                        store.setCompanyName(company.name)
                        e.target.setSelectionRange(
                          e.target.value.length,
                          e.target.value.length
                        )
                      }}
                      onBlur={onClickOutside}
                      onKeyDown={keyboardHandler({
                        enter: () => {
                          store.editSubmit()
                        },
                        escape: () => store.editCancel()
                      })}
                      value={state.companyName}
                      onChange={e => {
                        store.setCompanyName(e.target.value)
                      }}
                      placeholder={Lang.msg.companyPlaceholder}
                    />
                  </TbItemLabelTw>
                )}

                <div className="absolute top-0 right-8">
                  <TwActionIcon onClick={() => store.edit(company)}>
                    <EditIcon />
                  </TwActionIcon>
                  <TwActionIcon onClick={() => store.delete(company)} $delete>
                    <DeleteIcon />
                  </TwActionIcon>
                </div>
              </div>
            </TbItemTw>
          )
        }
      )}

      {Sync.getPendingCompanies().map(
        // eslint-disable-next-line max-lines-per-function
        company => {
          return (
            <TbItemTw
              draggable
              style={
                company.companyId === state.draggedCompany
                  ? {
                      transition: '0.01s',
                      transform: 'translateX(-9999px)'
                    }
                  : {}
              }
              key={company.companyId}
              className={cn(
                company.companyId === globals.getCompanyId() && 'font-semibold'
              )}
              $hoverTextMain={state.mouseEnter}
            >
              <TwIcon
                className={cn(
                  company.companyId === globals.getCompanyId()
                    ? 'text-secondary-constant-old'
                    : 'hover:text-action-hover'
                )}
                onMouseEnter={() => {
                  store.setMouseEnter(true)
                }}
                onMouseLeave={() => {
                  store.setMouseEnter(false)
                }}
              >
                <CompanyIcon />
              </TwIcon>
              <div className="flex w-full h-32 hover:text-main-old text-main-old">
                {state.edited !== company.companyId && (
                  <TbItemLabelTw
                    onClick={() => pendingCompanyUserStore.update()}
                  >
                    {company.companyName}
                  </TbItemLabelTw>
                )}
              </div>
            </TbItemTw>
          )
        }
      )}

      <TbItemTw onClick={() => store.editOpen()} className="cursor-pointer">
        <TwIcon className="cursor-pointer hover:text-action-hover text-action-default">
          <AddCompanyIcon />
        </TwIcon>
        <TbItemLabelTw className="cursor-pointer hover:text-action-hover text-action-default">
          {Lang.msg.addCompany}
        </TbItemLabelTw>
      </TbItemTw>

      {state.adding && (
        <AddCompany
          add={name => store.addingSubmit(name)}
          close={() => store.addingCancel()}
        />
      )}

      {state.showConfigure && (
        <TwConfigure onClick={() => store.configure()}>
          <ConfigIcon />
          <div className="w-4" />
          {Lang.msg.configureCompany3}
        </TwConfigure>
      )}
    </>
  )
})
